import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Activity, TrendingUp, Move, AlertTriangle, Ban, Plane, Wallet, BadgeCheck,
  Building, IdCard, LogOut, FileSignature, Award, RefreshCw,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { employeeTimelineAPI } from '../services/api';

// Maps the backend `icon` hint to a lucide-react icon; falls back to Activity.
const ICONS = {
  'trending-up': TrendingUp, move: Move, 'alert-triangle': AlertTriangle, ban: Ban,
  plane: Plane, wallet: Wallet, 'badge-check': BadgeCheck, building: Building,
  'id-card': IdCard, 'log-out': LogOut, 'file-signature': FileSignature, award: Award,
  activity: Activity,
};

const SOURCE_META = {
  lifecycle: { label: 'Lifecycle', variant: 'default' },
  exit: { label: 'Exit', variant: 'destructive' },
  contract: { label: 'Contract', variant: 'secondary' },
  recognition: { label: 'Recognition', variant: 'outline' },
};

const SOURCES = ['lifecycle', 'exit', 'contract', 'recognition'];

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

/**
 * Unified per-employee lifecycle timeline. Reusable — embed anywhere an
 * `employeeId` is known (Action Center, employee profile, etc.).
 */
const EmployeeTimeline = ({ employeeId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState('all');

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (active !== 'all') params.sources = active;
      const res = await employeeTimelineAPI.get(employeeId, params);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [employeeId, active]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          <Button variant={active === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setActive('all')}>All</Button>
          {SOURCES.map((s) => (
            <Button key={s} variant={active === s ? 'default' : 'outline'} size="sm" onClick={() => setActive(s)}>
              {SOURCE_META[s].label}
            </Button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
      </div>

      {loading && <div className="text-center text-muted-foreground py-8 text-sm">Loading timeline…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center text-muted-foreground py-8 text-sm">No lifecycle events recorded for this employee.</div>
      )}

      {!loading && items.length > 0 && (
        <ol className="relative border-l border-border ml-3">
          {items.map((it) => {
            const Icon = ICONS[it.icon] || Activity;
            const meta = SOURCE_META[it.source] || { label: it.source, variant: 'secondary' };
            return (
              <li key={it.id} className="mb-5 ml-6">
                <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-muted ring-4 ring-background">
                  <Icon className="h-3.5 w-3.5 text-foreground" />
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{it.title}</span>
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                  {it.status && <span className="text-xs text-muted-foreground">{titleize(it.status)}</span>}
                  <span className="text-xs text-muted-foreground ml-auto">{fmtDate(it.date)}</span>
                </div>
                {it.description && <p className="text-sm text-muted-foreground mt-1">{it.description}</p>}
                {it.actor && <p className="text-xs text-muted-foreground mt-0.5">By {it.actor}</p>}
              </li>
            );
          })}
        </ol>
      )}
      {!loading && data && (
        <div className="text-xs text-muted-foreground">{data.total} event(s) across the employee lifecycle.</div>
      )}
    </div>
  );
};

export default EmployeeTimeline;
